/**
 * Stop Manager Component
 * 
 * Material-UI adaptation of CirculatorWebpage StopManager.
 * Manages the master list of stops for fixed routes.
 */

import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Paper,
  Alert,
  Popover,
  MenuList,
  Card,
  CardContent,
  CardHeader,
  Chip,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';

import { useFixedRoutes } from '../../context/FixedRoutesContext';
import { AppContext, Point } from '../../context/context';
import type { FixedRouteStop } from '../../types/fixedRoutes';

import { Icon } from '@liftango/ui-icons';
export const StopManager: React.FC = () => {
  const { t } = useTranslation('fixedRoutes');
  const context = useContext(AppContext);
  const { stops, routes, addStop, updateStop, deleteStop } = useFixedRoutes();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [mapPointAnchorEl, setMapPointAnchorEl] = useState<HTMLElement | null>(null);
  const [mapPointSearchTerm, setMapPointSearchTerm] = useState('');
  const [stopToEdit, setStopToEdit] = useState<FixedRouteStop | null>(null);
  const [stopToDelete, setStopToDelete] = useState<FixedRouteStop | null>(null);
  const [stopName, setStopName] = useState('');
  const [stopLat, setStopLat] = useState('');
  const [stopLng, setStopLng] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [addMethod, setAddMethod] = useState<'manual' | 'map'>('manual');
  const [isPickingLocation, setIsPickingLocation] = useState(false);
  const [stopSearchQuery, setStopSearchQuery] = useState('');
  const [stopSortBy, setStopSortBy] = useState<'name-asc' | 'name-desc' | 'usage-desc' | 'usage-asc'>('name-asc');
  const [addFromLayerSelection, setAddFromLayerSelection] = useState('');

  const existingMapPointIds = useMemo(
    () => new Set(stops.filter((stop) => stop.mapPointId).map((stop) => stop.mapPointId as string)),
    [stops]
  );

  const allMapPoints = useMemo(() => {
    if (!context) {
      return [];
    }

    return context.global.mapLayers.flatMap((layer) =>
      layer.shapes
        .filter((shape): shape is Point => shape.type === 'point')
        .map((point) => ({
          id: point.id,
          name: point.name || point.id,
          coordinates: point.coordinates,
          layerId: point.layerId ?? layer.layerId,
        }))
    );
  }, [context]);

  const availableMapPoints = useMemo(
    () => allMapPoints.filter((point) => !existingMapPointIds.has(point.id)),
    [allMapPoints, existingMapPointIds]
  );

  const layerPointOptions = useMemo(() => {
    if (!context?.global.mapLayers) {
      return [];
    }

    return context.global.mapLayers
      .map((layer) => {
        const addablePoints = layer.shapes.filter(
          (shape): shape is Point => shape.type === 'point' && !existingMapPointIds.has(shape.id)
        );

        return {
          layerId: layer.layerId,
          layerName: layer.layerName,
          count: addablePoints.length,
        };
      })
      .filter((option) => option.count > 0);
  }, [context?.global.mapLayers, existingMapPointIds]);

  // Filter by search term
  const filteredMapPoints = availableMapPoints.filter(
    (point) =>
      point.name.toLowerCase().includes(mapPointSearchTerm.toLowerCase()) ||
      point.id.toLowerCase().includes(mapPointSearchTerm.toLowerCase())
  );

  const isMapPointDropdownOpen = Boolean(mapPointAnchorEl);

  // Calculate which stops are used in routes
  const stopsUsedInRoutes = useMemo(() => {
    const usedStopIds = new Set<string>();
    routes.forEach((route) => {
      route.stops.forEach((stopConfig) => {
        usedStopIds.add(stopConfig.stopId);
      });
    });
    return usedStopIds;
  }, [routes]);

  const handleAddClick = () => {
    setStopName('');
    setStopLat('');
    setStopLng('');
    setError(null);
    setAddMethod('manual');
    setIsPickingLocation(false);
    setIsAddDialogOpen(true);
  };

  // Listen for map clicks when in pick location mode
  useEffect(() => {
    if (!isPickingLocation || !isAddDialogOpen) return;

    const handleMapClick = (event: CustomEvent<{ lat: number; lng: number }>) => {
      const { lat, lng } = event.detail;
      setStopLat(lat.toFixed(6));
      setStopLng(lng.toFixed(6));
      setIsPickingLocation(false);
      setError(null);
    };

    window.addEventListener('pickStopLocation' as any, handleMapClick as EventListener);
    
    return () => {
      window.removeEventListener('pickStopLocation' as any, handleMapClick as EventListener);
    };
  }, [isPickingLocation, isAddDialogOpen]);

  const handleStartPickLocation = () => {
    setIsPickingLocation(true);
    setError(null);
    // Dispatch event to notify map to enter pick mode
    window.dispatchEvent(new CustomEvent('startPickStopLocation'));
  };

  const handleCancelPickLocation = () => {
    setIsPickingLocation(false);
    window.dispatchEvent(new CustomEvent('cancelPickStopLocation'));
  };

  const handleSelectFromMap = (event: React.MouseEvent<HTMLElement>) => {
    setMapPointAnchorEl(event.currentTarget);
    setMapPointSearchTerm('');
  };

  const handleCloseMapPointDropdown = () => {
    setMapPointAnchorEl(null);
    setMapPointSearchTerm('');
  };

  const handleSelectMapPoint = (point: typeof availableMapPoints[0]) => {
    const stop: FixedRouteStop = {
      id: `stop-${Date.now()}-${point.id}`,
      name: point.name,
      coordinates: {
        lat: point.coordinates[0],
        lng: point.coordinates[1],
      },
      mapPointId: point.id,
    };

    addStop(stop);
    handleCloseMapPointDropdown();
  };

  const createStopFromMapPoint = (point: Point, idSuffix: string): FixedRouteStop => ({
    id: `stop-${idSuffix}-${point.id}`,
    name: point.name || point.id,
    coordinates: {
      lat: point.coordinates[0],
      lng: point.coordinates[1],
    },
    mapPointId: point.id,
  });

  const addMapPointsAsStops = (points: Point[]) => {
    const baseTimestamp = Date.now();
    points.forEach((point, index) => {
      addStop(createStopFromMapPoint(point, `${baseTimestamp}-${index}`));
    });
  };

  const handleAddAllPoints = () => {
    if (!context) {
      return;
    }

    const pointsToAdd = context.global.mapLayers.flatMap((layer) =>
      layer.shapes.filter(
        (shape): shape is Point => shape.type === 'point' && !existingMapPointIds.has(shape.id)
      )
    );
    addMapPointsAsStops(pointsToAdd);
  };

  const handleAddPointsFromLayer = (layerId: number) => {
    const layer = context?.global.mapLayers.find((mapLayer) => mapLayer.layerId === layerId);
    if (!layer) {
      return;
    }

    const pointsToAdd = layer.shapes.filter(
      (shape): shape is Point => shape.type === 'point' && !existingMapPointIds.has(shape.id)
    );
    addMapPointsAsStops(pointsToAdd);
  };

  const renderAddFromLayerSelect = () => (
    <FormControl size="small" sx={{ minWidth: 160 }} disabled={layerPointOptions.length === 0}>
      <InputLabel id="stop-manager-add-from-layer-label">
        {t('stopManager.addFromLayer')}
      </InputLabel>
      <Select
        labelId="stop-manager-add-from-layer-label"
        value={addFromLayerSelection}
        onChange={(e) => {
          if (e.target.value === '') {
            return;
          }
          const layerId = Number(e.target.value);
          if (!Number.isFinite(layerId)) {
            return;
          }
          handleAddPointsFromLayer(layerId);
          setAddFromLayerSelection('');
        }}
        label={t('stopManager.addFromLayer')}
        MenuProps={{
          sx: { zIndex: (theme) => theme.zIndex.drawer + 5 },
          PaperProps: {
            sx: { zIndex: (theme) => theme.zIndex.drawer + 6 },
          },
        }}
      >
        {layerPointOptions.map((option) => (
          <MenuItem key={option.layerId} value={String(option.layerId)}>
            {t('stopManager.addFromLayerOption', {
              layerName: option.layerName,
              count: option.count,
            })}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const handleEditClick = (stop: FixedRouteStop) => {
    setStopToEdit(stop);
    setStopName(stop.name);
    setStopLat(stop.coordinates?.lat.toString() || '');
    setStopLng(stop.coordinates?.lng.toString() || '');
    setError(null);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (stop: FixedRouteStop) => {
    setStopToDelete(stop);
    setIsDeleteDialogOpen(true);
  };

  const validateCoordinates = (lat: string, lng: string): { lat: number; lng: number } | null => {
    if (!lat || !lng) return null;

    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);

    if (
      isNaN(latNum) ||
      isNaN(lngNum) ||
      latNum < -90 ||
      latNum > 90 ||
      lngNum < -180 ||
      lngNum > 180
    ) {
      setError(t('stopManager.errors.invalidCoordinates'));
      return null;
    }

    return { lat: latNum, lng: lngNum };
  };

  const handleSave = () => {
    if (!stopName.trim()) {
      setError(t('stopManager.errors.nameRequired'));
      return;
    }

    // Cancel pick mode if active
    if (isPickingLocation) {
      handleCancelPickLocation();
    }

    setError(null);
    const coordinates = validateCoordinates(stopLat, stopLng);
    if (stopLat || stopLng) {
      if (!coordinates) return; // Error already set by validateCoordinates
    }

    if (stopToEdit) {
      // Update existing stop
      updateStop({
        ...stopToEdit,
        name: stopName.trim(),
        coordinates: coordinates || stopToEdit.coordinates,
      });
      setIsEditDialogOpen(false);
      setStopToEdit(null);
    } else {
      // Add new stop
      if (!coordinates) {
        setError(t('stopManager.errors.coordinatesRequired'));
        return;
      }

      const newStop: FixedRouteStop = {
        id: `stop-${Date.now()}`,
        name: stopName.trim(),
        coordinates,
      };
      addStop(newStop);
      setIsAddDialogOpen(false);
    }

    // Reset form
    setStopName('');
    setStopLat('');
    setStopLng('');
    setIsPickingLocation(false);
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    if (isPickingLocation) {
      handleCancelPickLocation();
    }
    setStopName('');
    setStopLat('');
    setStopLng('');
    setError(null);
    setIsPickingLocation(false);
  };

  const handleConfirmDelete = () => {
    if (stopToDelete) {
      deleteStop(stopToDelete.id);
      setIsDeleteDialogOpen(false);
      setStopToDelete(null);
    }
  };

  return (
    <Card elevation={0} sx={{ boxShadow: 'none', border: 'none', backgroundColor: 'transparent' }}>
      <CardHeader
        sx={{ px: 0, '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' } }}
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <Icon size={20} name="location-dot" />
            <Typography variant="h6">{t('stopManager.title')}</Typography>
          </Box>
        }
        action={
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<Icon size={20} name="map" />}
              onClick={handleSelectFromMap}
              size="small"
            >
              {t('stopManager.fromMap')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Icon size={20} name="circle-plus" />}
              onClick={handleAddAllPoints}
              size="small"
              disabled={availableMapPoints.length === 0}
              title={t('stopManager.addAllPointsTooltip', { count: availableMapPoints.length })}
            >
              {t('stopManager.addAllPoints')}
            </Button>
            {renderAddFromLayerSelect()}
          </Box>
        }
        subheader={t('stopManager.subheader')}
      />
      <CardContent sx={{ px: 0 }}>
        {stops.length === 0 ? (
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              backgroundColor: 'background.default',
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('stopManager.noStopsAvailable')}
            </Typography>
            <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<Icon size={20} name="map" />}
                onClick={handleSelectFromMap}
                size="small"
              >
                {t('stopManager.fromMap')}
              </Button>
              <Button
                variant="contained"
                startIcon={<Icon size={20} name="circle-plus" />}
                onClick={handleAddAllPoints}
                size="small"
                disabled={availableMapPoints.length === 0}
                title={t('stopManager.addAllPointsTooltip', { count: availableMapPoints.length })}
              >
                {t('stopManager.addAllPoints')}
              </Button>
              {renderAddFromLayerSelect()}
            </Box>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Search and Sort Controls */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                placeholder={t('stopManager.searchPlaceholder')}
                value={stopSearchQuery}
                onChange={(e) => setStopSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon size={20} name="magnifying-glass" style={{ color: '#52565e' }} />
                    </InputAdornment>
                  ),
                  endAdornment: stopSearchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setStopSearchQuery('')}
                        edge="end"
                      >
                        <Icon name="xmark" size={20} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                size="small"
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t('stopManager.sortBy')}</InputLabel>
                <Select
                  value={stopSortBy}
                  onChange={(e) => setStopSortBy(e.target.value as typeof stopSortBy)}
                  label={t('stopManager.sortBy')}
                >
                  <MenuItem value="name-asc">{t('stopManager.sortOptions.nameAsc')}</MenuItem>
                  <MenuItem value="name-desc">{t('stopManager.sortOptions.nameDesc')}</MenuItem>
                  <MenuItem value="usage-desc">{t('stopManager.sortOptions.usageDesc')}</MenuItem>
                  <MenuItem value="usage-asc">{t('stopManager.sortOptions.usageAsc')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {/* Filtered Stops */}
            {(() => {
              const filteredStops = stops.filter((stop) => {
                if (!stopSearchQuery.trim()) return true;
                
                const query = stopSearchQuery.toLowerCase();
                const stopName = stop.name.toLowerCase();
                const coordinates = stop.coordinates
                  ? `${stop.coordinates.lat.toFixed(4)}, ${stop.coordinates.lng.toFixed(4)}`.toLowerCase()
                  : '';
                const mapPointId = (stop.mapPointId || '').toLowerCase();
                const stopId = stop.id.toLowerCase();
                
                // Check if stop is used in routes
                const isUsedInRoutes = stopsUsedInRoutes.has(stop.id);
                const routeCount = routes.filter((route) =>
                  route.stops.some((stopConfig) => stopConfig.stopId === stop.id)
                ).length;
                const routeUsageText = isUsedInRoutes ? `used in ${routeCount} route${routeCount > 1 ? 's' : ''}` : '';
                
                // Search route names that use this stop
                const routeNamesMatch = routes
                  .filter((route) => route.stops.some((stopConfig) => stopConfig.stopId === stop.id))
                  .some((route) => route.name.toLowerCase().includes(query));
                
                return (
                  stopName.includes(query) ||
                  coordinates.includes(query) ||
                  mapPointId.includes(query) ||
                  stopId.includes(query) ||
                  routeUsageText.includes(query) ||
                  routeNamesMatch
                );
              });

              if (filteredStops.length === 0) {
                return (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('stopManager.noMatch', { query: stopSearchQuery })}
                    </Typography>
                  </Box>
                );
              }

              // Group stops by name and coordinates to deduplicate
              const stopGroups = new Map<string, {
                stop: FixedRouteStop;
                stopIds: string[];
                routeCount: number;
                routeNames: string[];
              }>();

              filteredStops.forEach((stop) => {
                // Create a key from name and coordinates
                const coordsKey = stop.coordinates
                  ? `${stop.coordinates.lat.toFixed(4)},${stop.coordinates.lng.toFixed(4)}`
                  : 'no-coords';
                const groupKey = `${stop.name.toLowerCase()}|${coordsKey}`;
                
                if (!stopGroups.has(groupKey)) {
                  // Calculate route count for this stop
                  const routesUsingThisStop = routes.filter((route) =>
                    route.stops.some((stopConfig) => stopConfig.stopId === stop.id)
                  );
                  
                  stopGroups.set(groupKey, {
                    stop,
                    stopIds: [stop.id],
                    routeCount: routesUsingThisStop.length,
                    routeNames: routesUsingThisStop.map(r => r.name),
                  });
                } else {
                  // Add this stop ID to the group and update route count
                  const group = stopGroups.get(groupKey)!;
                  if (!group.stopIds.includes(stop.id)) {
                    group.stopIds.push(stop.id);
                    
                    // Add routes that use this stop ID
                    const routesUsingThisStop = routes.filter((route) =>
                      route.stops.some((stopConfig) => stopConfig.stopId === stop.id)
                    );
                    
                    routesUsingThisStop.forEach(route => {
                      if (!group.routeNames.includes(route.name)) {
                        group.routeNames.push(route.name);
                      }
                    });
                    
                    // Update route count (unique routes)
                    group.routeCount = group.routeNames.length;
                  }
                }
              });

              // Sort stop groups
              const sortedStopGroups = Array.from(stopGroups.values()).sort((a, b) => {
                switch (stopSortBy) {
                  case 'name-asc':
                    return a.stop.name.localeCompare(b.stop.name);
                  case 'name-desc':
                    return b.stop.name.localeCompare(a.stop.name);
                  case 'usage-desc':
                    return b.routeCount - a.routeCount;
                  case 'usage-asc':
                    return a.routeCount - b.routeCount;
                  default:
                    return 0;
                }
              });

              return sortedStopGroups.map((group) => {
              const stop = group.stop;
              const isUsedInRoutes = group.routeCount > 0;
              const routeCount = group.routeCount;

              return (
                <Paper
                  key={group.stopIds.join(',')}
                  elevation={1}
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderRadius: 1,
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {stop.name}
                      </Typography>
                      {group.stopIds.length > 1 && (
                        <Chip
                          label={t('stopManager.instancesChip', { count: group.stopIds.length })}
                          size="small"
                          color="default"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                      {stop.mapPointId && (
                        <Chip
                          icon={<Icon size={20} name="map" />}
                          label={t('stopManager.fromMapChip')}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20 }}
                        />
                      )}
                      {isUsedInRoutes && (
                        <Chip
                          icon={<Icon size={20} name="route" />}
                          label={t('stopManager.usedInRoutes', { count: routeCount })}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ height: 20 }}
                        />
                      )}
                    </Box>
                    {stop.coordinates ? (
                      <Typography variant="body2" color="text.secondary">
                        {stop.coordinates.lat.toFixed(4)}, {stop.coordinates.lng.toFixed(4)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="error">
                        {t('stopManager.noCoordinates')}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      onClick={() => handleEditClick(stop)}
                      size="small"
                      aria-label={t('stopManager.editAria', { name: stop.name })}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.selected',
                        },
                      }}
                    >
                      <Icon name="pen-to-square" size={20} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteClick(stop)}
                      size="small"
                      aria-label={t('stopManager.deleteAria', { name: stop.name })}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'error.light',
                          color: 'error.contrastText',
                        },
                      }}
                    >
                      <Icon name="trash" size={20} />
                    </IconButton>
                  </Box>
                </Paper>
              );
            });
            })()}
          </Box>
        )}
      </CardContent>

      {/* Map Point Dropdown */}
      <Popover
        open={isMapPointDropdownOpen}
        anchorEl={mapPointAnchorEl}
        onClose={handleCloseMapPointDropdown}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          root: {
            sx: { zIndex: (theme) => theme.zIndex.drawer + 5 },
          },
        }}
        PaperProps={{
          sx: {
            zIndex: (theme) => theme.zIndex.drawer + 6,
            minWidth: 300,
            maxWidth: 400,
            maxHeight: 500,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('stopManager.popover.title')}
          </Typography>
          <TextField
            fullWidth
            placeholder={t('stopManager.popover.searchPlaceholder')}
            value={mapPointSearchTerm}
            onChange={(e) => setMapPointSearchTerm(e.target.value)}
            size="small"
            autoFocus
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon name="magnifying-glass" size={20} />
                </InputAdornment>
              ),
              endAdornment: mapPointSearchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setMapPointSearchTerm('')}
                    edge="end"
                  >
                    <Icon name="xmark" size={20} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          {availableMapPoints.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {t('stopManager.popover.noPointsAvailable')}
              </Typography>
            </Box>
          ) : filteredMapPoints.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {t('stopManager.popover.noPointsMatch')}
              </Typography>
            </Box>
          ) : (
            <MenuList sx={{ p: 0 }}>
              {filteredMapPoints.map((point) => (
                <Box
                  key={point.id}
                  component="button"
                  onClick={() => handleSelectMapPoint(point)}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Icon size={20} name="location-dot" style={{ color: '#ef7335', flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="medium" noWrap>
                      {point.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {point.coordinates[0].toFixed(4)}, {point.coordinates[1].toFixed(4)}
                    </Typography>
                  </Box>
                  <Icon name="plus" size={20} style={{ flexShrink: 0 }} />
                </Box>
              ))}
            </MenuList>
          )}
        </Box>
      </Popover>

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('stopManager.addDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {t('stopManager.addDialog.description')}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {isPickingLocation && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('stopManager.addDialog.pickInfo')}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={t('stopManager.addDialog.stopName')}
            fullWidth
            variant="outlined"
            value={stopName}
            onChange={(e) => setStopName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <Box display="flex" gap={2} alignItems="flex-start">
            <TextField
              margin="dense"
              label={t('stopManager.addDialog.latitude')}
              type="number"
              fullWidth
              variant="outlined"
              value={stopLat}
              onChange={(e) => setStopLat(e.target.value)}
              inputProps={{ step: 'any' }}
              placeholder="34.0928"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleStartPickLocation}
                      edge="end"
                      title={t('stopManager.addDialog.pickOnMap')}
                      color={isPickingLocation ? 'primary' : 'default'}
                      sx={{
                        backgroundColor: isPickingLocation ? 'action.selected' : 'transparent',
                      }}
                    >
                      <Icon size={20} name="location-dot" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="dense"
              label={t('stopManager.addDialog.longitude')}
              type="number"
              fullWidth
              variant="outlined"
              value={stopLng}
              onChange={(e) => setStopLng(e.target.value)}
              inputProps={{ step: 'any' }}
              placeholder="-118.3287"
            />
          </Box>
          {isPickingLocation && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancelPickLocation}
                size="small"
                startIcon={<Icon size={20} name="xmark" />}
              >
                {t('stopManager.addDialog.cancelMapSelection')}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} variant="contained" disabled={!stopName.trim()}>
            {t('stopManager.addDialog.addStop')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('stopManager.editDialog.title')}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={t('stopManager.addDialog.stopName')}
            fullWidth
            variant="outlined"
            value={stopName}
            onChange={(e) => setStopName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <Box display="flex" gap={2}>
            <TextField
              margin="dense"
              label={t('stopManager.addDialog.latitude')}
              type="number"
              fullWidth
              variant="outlined"
              value={stopLat}
              onChange={(e) => setStopLat(e.target.value)}
              inputProps={{ step: 'any' }}
              placeholder="34.0928"
            />
            <TextField
              margin="dense"
              label={t('stopManager.addDialog.longitude')}
              type="number"
              fullWidth
              variant="outlined"
              value={stopLng}
              onChange={(e) => setStopLng(e.target.value)}
              inputProps={{ step: 'any' }}
              placeholder="-118.3287"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} variant="contained" disabled={!stopName.trim()}>
            {t('stopManager.editDialog.saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>{t('common.areYouSure')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('stopManager.deleteDialog.message', { name: stopToDelete?.name ?? '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default StopManager;

